/**
 * Level - a collection of actors loaded from serialized world data.
 * Uses WeakRef to avoid preventing garbage collection of actors and world.
 */
import { ObjectTracker } from '../systems/ObjectTracker.js';

import type { World } from './World.js';
import type { Actor } from '../actors/Actor.js';
import type { Constructor } from '../systems/ClassRegistry.js';

export class Level {
  private readonly actorRefs: WeakRef<Actor>[] = [];
  private readonly worldRef: WeakRef<World>;

  constructor(world: World, actors: Actor[] = []) {
    ObjectTracker.track(this);
    this.worldRef = new WeakRef(world);
    this.actorRefs = actors.map(actor => new WeakRef(actor));
  }

  public get world(): World | undefined {
    return this.worldRef.deref();
  }

  public get actors(): Actor[] {
    return this.actorRefs
      .map(ref => ref.deref())
      .filter((actor): actor is Actor => actor !== undefined);
  }

  public addActor(actor: Actor): void {
    this.actorRefs.push(new WeakRef(actor));
  }

  public getActorsOfType<T extends Actor>(type: Constructor<T>): T[] {
    return this.actors.filter((actor): actor is T => actor instanceof type);
  }

  public unload(): void {
    const world = this.world;
    if (world) {
      world.removeActors(...this.actors);
    }
  }
}
